import cn from 'classnames';
import * as React from 'react';
import { isEqual } from 'lodash-es';
import { useDispatch } from 'react-redux';
import { generatePath } from 'react-router';

import { Search, Venues, VendorsApi } from '@weddingspot/ws-api-client';
import { generateContactLink, nameToUrlName } from './utils';
import * as URLS from '../../urls';
import { setVenueFavorite, unsetVenueFavorite } from '../../actions/venue-profile';
import { AnalyticsEvent, logEvent } from '@weddingspot/ws-analytics';
import { useEffect, useState } from 'react';
import OldBottomRow from './VenueCard/OldBottomRow';
import CardBottomRow from './VenueCard/CardBottomRow';
import VenueCardLocation from './VenueCard/VenueCardLocation';
import { HeaderContext, WSAppContext, FeatureFlagContext } from '@weddingspot/ws-core-components';

import { MapPinIcon, HeartIcon, HeartFillIcon } from '@cvent/carina/components/Icon';
import { OnWhen } from '@cvent/carina/components/OnWhen';
import { SRP_VIEWS } from '../../constants';

const _styles = require('./venueCard.scss');

const fallbackImg = require('../../assets/images/rings.svg');

const { default: LazyLoad } = require('react-lazyload');

export interface VenueCardProps {
    // Venue data
    venue: Search.SearchResult;

    // User's spot estimate for this venue if available
    spotEstimate?: number | null;

    // Wether or not this venue is a favorite for the current user
    isFavorite?: boolean;

    // Event handler for when card is clicked
    handleSearchResultClick?: (venue: Search.SearchResult, position: number) => void;

    // External styling props
    classes?: {
        wrapper?: string;
        title?: string;
    };

    // Current SRP variant
    srpVariant?: 'affordable' | 'rd';

    // If true, will wait to downloading images until after all other resources
    // on the page have been downloaded (window.onload)
    deferImageLoad?: boolean;
    isDestinationWeddings?: boolean;

    // Position in grid if applicable
    position: number;

    // Data for analytics event
    // tslint:disable-next-line:no-any
    metadata?: any;
    personalizedData?: Search.SearchPersonalizedData;
    viewType?: string;
    referredViaAd?: boolean;
    srpView?: string;
}

const VenueCardPicture = (props: { venue: Search.SearchResult; viewType?: string; lazyLoad?: boolean }) => {
    const [failed, setFailed] = useState<boolean>();

    let objDataSrc;
    let cventImgSrc;
    try {
        const imgSrc = props.venue.images[0];
        if (imgSrc) {
            let imgUrl = new URL(imgSrc);
            if (imgUrl.origin.match(/images-?.*\.cvent\.com/)) {
                imgUrl.search = 'f=webp&unique=true&d=490';
                cventImgSrc = imgUrl.href;
                objDataSrc = imgSrc + '?unique=true';
            } else {
                objDataSrc = imgSrc;
            }
        }
    } catch {
        // URL couldn't be parsed/modified so just use whatever was passed in
        objDataSrc = props.venue.images[0];
    }

    const lazyLoad = (children: React.ReactNode) => {
        if (props.lazyLoad) {
            return (
                <LazyLoad key={props.venue.id} offset={200} once={true} overflow={props.viewType === SRP_VIEWS.MAP}>
                    {children}
                </LazyLoad>
            );
        } else {
            return <>{children}</>;
        }
    };

    return (
        <div className={_styles.cardImage}>
            {lazyLoad(
                <picture>
                    {cventImgSrc && <source srcSet={cventImgSrc} type='image/webp' />}
                    {!failed && (
                        <img
                            src={objDataSrc}
                            alt={`${props.venue.name} wedding ${props.venue.region}`}
                            width={490}
                            onError={(event) => {
                                setFailed(true);
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                        />
                    )}

                    {failed && <img src={fallbackImg} className={cn(_styles.fallback)} alt='Venue Image'/>}
                </picture>
            )}
        </div>
    );
};

export const VenueCard = React.memo((props: VenueCardProps) => {
    const {
        venue,
        isFavorite,
        spotEstimate,
        srpVariant,
        handleSearchResultClick,
        deferImageLoad,
        isDestinationWeddings,
        position,
        metadata,
        viewType,
        referredViaAd,
        srpView
    } = props;

    const classes = props.classes || {};

    const dispatch = useDispatch();
    const { userIsAuthenticated, openAuthModal } = React.useContext(WSAppContext);

    const [_isFavorite, setIsFavorite] = React.useState(isFavorite);

    React.useEffect(() => {
        setIsFavorite(isFavorite);
    }, [isFavorite]);

    let isRD = srpVariant === 'rd';
    let contactAvailable = !!spotEstimate && !isRD;
    let favoriteType = isRD ? Venues.FavoriteType.REHEARSAL_DINNERS : Venues.FavoriteType.WEDDING;

    const [isFavoriteUpdating, setIsFavoriteUpdating] = React.useState<boolean>(false);

    const { profileVersion } = React.useContext(HeaderContext);
    const { flagEnabled } = React.useContext(FeatureFlagContext);
    const { setSRPViewInSessionStorage, removeSRPViewInSessionStorage } = React.useContext(WSAppContext);

    const [showShoppingCart, setShowShoppingCart] = useState(false);
    useEffect(() => {
        setShowShoppingCart(profileVersion === 'v3');
    }, [profileVersion]);

    const updateFavorite = () => {
        setIsFavoriteUpdating(true);

        if (!_isFavorite) {
            VendorsApi.setFavorite([venue.id], favoriteType)
                .then((res) => {
                    dispatch(setVenueFavorite());
                    setIsFavorite(!_isFavorite);
                })
                .catch()
                .then(() => setIsFavoriteUpdating(false));
        } else {
            VendorsApi.removeFavorite([venue.id])
                .then((res) => {
                    dispatch(unsetVenueFavorite());
                    setIsFavorite(!_isFavorite);
                })
                .catch()
                .then(() => setIsFavoriteUpdating(false));
        }
    };

    let profileUrl = generatePath(URLS.VENUE_PROFILE_NAME, {
        id: venue.id,
        name: nameToUrlName(venue.name),
    });
    if (isRD) {
        profileUrl += '?rdv=1';
    }

    // Defer image load
    const [startImageLoad, setStartImageLoad] = React.useState(!deferImageLoad);

    React.useDebugValue(startImageLoad ? 'Start Load' : 'Defer Load');

    const handleLoad = () => {
        setStartImageLoad(true);
    };

    React.useEffect(() => {
        const alreadyLoaded = typeof document !== 'undefined' && document.readyState === 'complete';
        if (alreadyLoaded && !startImageLoad) {
            handleLoad();
        } else {
            window.addEventListener('load', handleLoad);
            return () => {
                window.removeEventListener('load', handleLoad);
            };
        }

        return;
    }, []);

    const logAmplitudeEvent = (eventName: AnalyticsEvent) => {
        const params: object = {
            VenueId: venue.id,
            PageNumber: metadata ? metadata.page : 1,
            Market: metadata && metadata.metadata.location ? metadata.metadata.location.market : 'All venues',
            Region: metadata && metadata.metadata.location ? metadata.metadata.location.region : null,
            Style: metadata && metadata.metadata.location ? metadata.metadata.location.style : null,
            Source: `SRP ${viewType} view`,
            SRPVersion: 2,
            ReferredViaAd: referredViaAd,
            SRPView: srpView,
        };
        logEvent(eventName, params);
    };

    const handlePriceVenueClick = (e: React.SyntheticEvent) => {
        const target = contactAvailable ? generateContactLink(venue.id) : venue.cta_button.url;
        setSRPViewInSessionStorage(venue.id, srpView ? srpView : null);
        window.open(target, '_blank');
        removeSRPViewInSessionStorage(venue.id);

        switch (venue.cta_button.label) {
            case 'Price venue':
                logAmplitudeEvent(AnalyticsEvent.EVTNM_CLICKED_DIRECTORY_PRICING_BUTTON);
                break;
            case 'Request quote':
                logAmplitudeEvent(AnalyticsEvent.EVTNM_VENUE_CARD_CTA_REQUEST_QUOTE);
                break;
            default:
                logAmplitudeEvent(AnalyticsEvent.EVTNM_VENUE_CARD_CTA_CLICK);
        }

        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
    };

    const handleFavoriteClick = (e: React.SyntheticEvent) => {
        if (userIsAuthenticated) {
            if (!isFavoriteUpdating) {
                updateFavorite();
            }
        } else {
            openAuthModal();
        }

        logAmplitudeEvent(AnalyticsEvent.EVTNM_CLICKED_FAVORITES);

        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
    };

    const handleOnClick = (link: string) => (e: React.SyntheticEvent) => {
        !!handleSearchResultClick && handleSearchResultClick(venue, position);
        setSRPViewInSessionStorage(venue.id, srpView ? srpView : null);
        window.open(link, '_blank');
        removeSRPViewInSessionStorage(venue.id);

        logAmplitudeEvent(AnalyticsEvent.EVTNM_VENUE_CARD_CLICKED);
        e.preventDefault();
    };
    return (
        <div
            className={cn(_styles.wrapper, classes.wrapper, { [_styles.round]: showShoppingCart })}
            itemScope={true}
            data-id={venue.id}
            itemType={'http://schema.org/LocalBusiness'}
        >
            <a onClick={handleOnClick(profileUrl)} href={profileUrl} target='_blank'>
                {venue.award_winner && (
                    <div className={_styles.awardWinner} itemProp='name'>
                        <span>Award Winner</span>
                    </div>
                )}

                <VenueCardPicture venue={venue} viewType={viewType} lazyLoad={position > 0} />

                <div className={_styles.footer}>
                    <div className={_styles.titleRow}>
                        <div className={cn(_styles.titleLeft, { [_styles.titleLeft_listView]: srpView == SRP_VIEWS.LIST})}>
                            <div className={cn({ [_styles.title]: srpView == SRP_VIEWS.GRID}, classes.title, { [_styles.title_listView]: srpView == SRP_VIEWS.LIST})}>{venue.name}</div>
                            <div className={_styles.location}>
                                <MapPinIcon color='#69717A' size='s' />
                                <VenueCardLocation isDestinationWeddings={isDestinationWeddings} venue={venue} />
                            </div>
                        </div>
                        {!flagEnabled('favorite_button_hidden_on_SRP') && (
                            <div className={_styles.titleRight}>
                                <div
                                    className={cn(_styles.favorite, { [_styles.animated]: isFavoriteUpdating })}
                                    onClick={handleFavoriteClick}
                                >
                                    {_isFavorite ? <HeartFillIcon size='m' color='#D5A0B8' /> : <HeartIcon size='m' color={(srpView == SRP_VIEWS.LIST)? '#881155' : '#AAAAAA'} />}
                                </div >
                            </div>
                        )}
                    </div>
                    <div className={'onWhenM'}>
                        <OnWhen m={true} l={true} xl={true}>
                            <p className={_styles.description}>{venue.description}</p>
                        </OnWhen>
                    </div>
                    {showShoppingCart ? (
                        <CardBottomRow venue={venue} contactAvailable={contactAvailable} handlePriceVenueClick={handlePriceVenueClick} srpView={srpView}/>
                    ) : (
                        <OldBottomRow venue={venue} contactAvailable={contactAvailable} handlePriceVenueClick={handlePriceVenueClick} srpView={srpView}/>
                    )}
                </div>
            </a>
        </div>
    );
}, isEqual);
